import {
  AppointmentSyncStatus,
  EligibilityStatus,
  EligibilityStatusOverride,
  EligibilityVerificationSource
} from './appointment.model';
import { InsuranceStatus, InsuranceStatusOverride } from './insurance.model';

import { EligibilityDataSource } from './data-source.model';
import { Member } from './patients.model';

export type ClaimHistory = {
  availedOn: string;
  procedureName: string;
  procedureCode: string;
  totalFund: number;
  usedFund: number;
  comments: string;
};

export type EligibilityLog = {
  appointmentId: number;
  eligibilityId: number;
  refId: string | null;
  insuranceId: number;
  runDate: Date;
  appointmentAt: Date;
  procedureCodes: string[];
  syncStatus: AppointmentSyncStatus;
  eligibilityStatus: EligibilityStatus;
  errors: {
    schedulingError: string[];
    dataSourceError: string;
  };
  eligibilityStatusOverride?: EligibilityStatusOverride | null;
  insuranceStatus: InsuranceStatus;
  insuranceStatusOverride?: InsuranceStatusOverride | null;
  eligibilityVerificationSource?: EligibilityVerificationSource | null;
};

export type CheckPatientEligibility = {
  firstName: string;
  lastName: string;
  dob: Date;
  zipcode: string;
  appointmentAt?: Date;
  providerId: number;
  payerId: number;
  procedureCodes: string[];
  groupNumber: string;
  memberId: number;
};

// Eligibility Details
export type EligibilityCoverage = {
  memberId: string;
  groupNumber: string;
};

// Eligibility Maximum and Deductibles
export type EligibilityMaxAndDed = {
  amount?: string | null;
  remaining?: string | null;
  category: string;
  periodUnit?: string | null;
  networkName?: string | null;
  networkType?: string | null;
  coverageLevel?: string | null;
  source?: string | null;
  notes?: Note[] | null;
};

export type EligibilityInsurance = {
  payerId: string;
  payerName: string;
  planName?: string | null;
  insuranceType?: string | null;
  planGroupName?: string | null;
  planType?: string | null;
  planStatus?: InsuranceStatus;
  planGroupNumber?: string | null;
  renewalType?: string | null;
  startDate?: string | null;
  effectiveDate?: string | null;
  subscriberCoveage: EligibilityCoverage;
  patientCoveage: EligibilityCoverage;
  maximums: Record<string, CvgLvlMaxAndDed>;
  deductibles: Record<string, CvgLvlMaxAndDed>;
  payerNotes?: Note[] | null;
  planNotes?: Note[] | null;
  payerAddress?: {
    address1?: string | null;
    address2?: string | null;
    city?: string | null;
    state?: string | null;
    zipCode?: string | null;
  };
};

export type CvgLvlMaxAndDed = Record<string, EligibilityMaxAndDed[]>;

export enum BenefitNoteTypeEnum {
  BenefitNotes = 'benefitNotes',
  AgeLimitNotes = 'ageLimitNotes',
  FrequencyNotes = 'frequencyNotes',
  WaitingNotes = 'waitingNotes',
  ProcedureNotes = 'procedureNotes',
  DeductibleNotes = 'deductibleNotes'
}

export enum DisplayMaxDedEnum {
  networkType = 'networkType',
  coverageLevel = 'coverageLevel',
  amount = 'amount',
  category = 'category',
  remaining = 'remaining',
  periodUnit = 'periodUnit',
  networkName = 'networkName',
  notes = 'notes'
}

export type EligibilityBenefits = {
  networkName?: string | null;
  coInsurance?: string | null;
  payerCoverage?: string | null;
  coPay?: string | null;
  procedureCode: string;
  procedureDescription?: string | null;
  visitLimit?: string | null;
  visitPeriod?: string | null;
  visitPeriodUnit?: string | null;
  ageMinimum?: string | null;
  ageMaximum?: string | null;
  lastVisit?: string | null;
  category?: string | null;
  source?: string | null;
  note?: string | null;
  waiting?: NormBenefitWaiting | null;
  deductible?: NormBenefitDeductible | null;
  alternate?: NormBenefitAlternate | null;
  benefitNotes?: {
    benefitNotes?: Note[] | null;
    ageLimitNotes?: Note[] | null;
    frequencyNotes?: Note[] | null;
    waitingNotes?: Note[] | null;
    procedureNotes?: Note[] | null;
    deductibleNotes?: Note[] | null;
  };
};

export type NormBenefitWaiting = {
  applies?: boolean;
  period?: string | null;
  unit?: string | null;
  notes?: Note[] | null;
};

export type NormBenefitDeductible = {
  applies?: boolean;
  notes?: Note[] | null;
};

export type NormBenefitAlternate = {
  exists?: boolean;
  notes?: Note[] | null;
};

export type EligibilityBenefitCategory = {
  category?: string | null;
  benefits: Record<string, EligibilityBenefits[]>;
};

export interface EligibilityPlanNotesAppendices {
  type: string;
  message: string;
}

export type EligibilityBenefitPerNetworkName = Record<string, EligibilityBenefits[]>;

export type TransformedEligibilityBenefitPerCategory = Record<
  string,
  Record<string, EligibilityBenefitPerNetworkName>
>;

export type ServiceHistory = {
  codes: string[];
  serviceDate: string;
  toothDetails?: { code: string; tooth: string | null }[];
};

export type NormalisedEligibilityDetails = {
  patient: Member;
  subscriber: Member;
  insurance: EligibilityInsurance;
  benefits: EligibilityBenefitCategory[];
  serviceHistory: ServiceHistory[];
  excludedProcedures?: string[];
};

export interface EligibilityDataSourceErrorObject {
  code: string;
  text: string;
  message?: string | null;
  isClientFriendly?: boolean;
}

export interface EligibilityErrorWithMessageObject {
  text: string;
  message: string;
  showErrorMessage: boolean;
}

// Errors that are shown to the user
export interface EligibilityDataSourceError {
  eligibilityScheduleId: number;
  dataSource: EligibilityDataSource;
  errors?: EligibilityDataSourceErrorObject[] | null;
}

interface ErrorToBeNotifiedObj {
  priority: number;
  eligibilityDataSource: EligibilityDataSource;
  title?: string;
  type?: 'warning' | 'danger' | 'success' | 'info';
  errorMessage?: string;
  content?: string;
  action?: {
    buttonText: string;
    navigateTo: string;
  };
}

// Eligibility Error setting related types
export type EligibilityErrorSetting = {
  // The numerical order priority for data source errors
  priority: Record<number, EligibilityDataSource>;
  // This is an array of Data sources
  // If a datasource is listed in this array,
  //      its error will not be shown in UI if the Eligibility is successs
  secondaryDataSources: EligibilityDataSource[];
  // Primary datasource
  primaryDataSources: EligibilityDataSource[];
  errorToBeNotifiedPriority: Record<string, ErrorToBeNotifiedObj>;
};

//! This has to be in sync with backend
export enum NetworkTypeEnum {
  inNetwork = 'inNetwork',
  outOfNetwork = 'outOfNetwork',
  networkNotApplicable = 'networkNotApplicable'
}

export const NetworkTypeUiMap: { [key: string]: string } = {
  'In Network': 'inNetwork',
  'in network': 'inNetwork',
  'Out Of Network': 'outOfNetwork',
  'out of network': 'outOfNetwork',
  'Out of Network': 'outOfNetwork',
  'Network Not Applicable': 'networkNotApplicable',
  'network not applicable': 'networkNotApplicable'
};

export const PeriodUiMap: { [key: string]: string } = {
  MONTH: 'Month',
  YEAR: 'Year',
  DAY: 'Day',
  WEEK: 'Week'
};

//! This has to be in sync with backend
export enum CoverageLevelEnum {
  family = 'family',
  individual = 'individual',
  unknown = 'unknown'
}

//! This has to be in sync with backend
export enum PayerBenefitManualEntryKeys {
  CoPay = 'coPay',
  CoInsurance = 'coInsurance',
  PayerCoverage = 'payerCoverage',
  LastVisit = 'lastVisit',
  VisitLimit = 'visitLimit',
  VisitPeriod = 'visitPeriod',
  VisitPeriodUnit = 'visitPeriodUnit',
  ageMinimum = 'ageMinimum',
  AgeMaximum = 'ageMaximum',
  Note = 'note'
}

export type BenefitEntry = {
  benefitKey: string;
  benefitValue: string;
};

export type UpsertPayerBenefitManually = {
  eligibilityId: number;
  networkType: NetworkTypeEnum;
  networkName?: string | null;
  procedureCode: string;
  benefits: BenefitEntry[];
};

export type DeleteManualPayerBenefit = {
  eligibilityId: number;
  networkType: NetworkTypeEnum;
  networkName?: string | null;
  procedureCode: string;
};

export type NetworkSwitchKey = keyof typeof NetworkTypeEnum;

export type SecondaryResult = {
  serviceSource: EligibilityServiceSource;
  dataSource: EligibilityDataSource;
  dataType: EligibilityDataTypes;
  data: string;
};

export enum EligibilityServiceSource {
  DentalXChange = 'DentalXChange',
  TuuthFairy = 'TuuthFairy',
  DentalXChangeEDP = 'DentalXChangeEDP'
}

export enum EligibilityServiceSourceNumber {
  DentalXChange = 1,
  TuuthFairy = 2,
  DentalXChangeEDP = 3
}

export enum EligibilityDataTypes {
  HTML = 'HTML'
}

export type Note = {
  type?: string | null;
  message?: string | string[] | null;
};

export enum PeriodUnitEnum {
  Lifetime = 'lifetime',
  ServiceYear = 'serviceYear',
  CoverageYear = 'coverageYear',
  Year = 'year',
  Month = 'month',
  Day = 'day',
  Visit = 'visit'
}

export enum MaxDedEnum {
  Maximum = 'maximum',
  Deductible = 'deductible'
}

export enum EligibilityBenefitEditRequestTypes {
  Maximum = 'maximum',
  Deductible = 'deductible',
  Coverage = 'coverage',
  PlanNotes = 'planNotes',
  NetworkNotApplicable = 'networkNotApplicable'
}

export type EligibilityBenefitEditResponse = Record<
  EligibilityBenefitEditRequestTypes,
  EligibilityMaxDedEdit[]
>;

export interface EligibilityMaxDedEdit {
  networkType: NetworkTypeEnum;
  networkName: string;
  config: MaximumDeductibleEditConfig;
  edits: {
    key: EligibilityBenefitEditKeys;
    value: string;
  }[];
  isProcessed?: boolean;
}

export interface MaximumDeductibleEditConfig {
  coverageLevel?: string;
  category?: string;
  amount?: string;
  remaining?: string;
  periodUnit?: string;
  payerCoverage?: string;
  coInsurance?: string;
  coPay?: string;
  ageMinimum?: string;
  ageMaximum?: string;
  visitLimit?: string;
  visitPeriod?: string;
  visitPeriodUnit?: string;
  field?: string;
  notes?: Note[];
  procedureCode?: string;
  procedureCodeDescription?: string;
  waiting?: boolean;
  deductible?: boolean;
  alternate?: boolean;
}
export interface CreateEligibilityBenefitEditRequestBody {
  networkType: NetworkTypeEnum;
  networkName: string | null;
  config: {
    coverageLevel?: string;
    category?: string;
    amount?: string;
    remaining?: string;
    periodUnit?: string;
    payerCoverage?: string;
    coInsurance?: string;
    coPay?: string;
    ageMinimum?: string;
    ageMaximum?: string;
    visitLimit?: string;
    visitPeriod?: string;
    visitPeriodUnit?: string;
    field?: string;
    notes?: Note[] | null;
    procedureCode?: string;
    waiting?: boolean;
    deductible?: boolean;
    alternate?: boolean;
  };
}

export enum EligibilityBenefitEditKeys {
  Amount = 'amount',
  Remaining = 'remaining',
  PeriodUnit = 'periodUnit',
  NetworkName = 'networkName',
  NetworkType = 'networkType',
  CoverageLevel = 'coverageLevel',
  Category = 'category',
  Notes = 'notes',
  PayerCoverage = 'payerCoverage',
  CoInsurance = 'coInsurance',
  CoPay = 'coPay',
  AgeMinimum = 'ageMinimum',
  AgeMaximum = 'ageMaximum',
  VisitPeriodUnit = 'visitPeriodUnit',
  VisitLimit = 'visitLimit',
  VisitPeriod = 'visitPeriod'
}

export enum MaxDedEditKeys {
  Amount = 'amount',
  Remaining = 'remaining',
  PeriodUnit = 'periodUnit',
  NetworkName = 'networkName',
  NetworkType = 'networkType',
  CoverageLevel = 'coverageLevel',
  Notes = 'notes',
  PeriodAmountRemaining = 'periodAmountRemaining'
}

export interface UpsertEligibilityBenefitEditRequestBody {
  networkType: NetworkTypeEnum;
  networkName: string;
  key: EligibilityBenefitEditKeys;
  value: string;
  config: {
    coverageLevel?: string;
    category?: string;
    amount?: string;
    remaining?: string;
    periodUnit?: string;
    payerCoverage?: string;
    coInsurance?: string;
    coPay?: string;
    ageMinimum?: string;
    ageMaximum?: string;
    visitLimit?: string;
    visitPeriod?: string;
    visitPeriodUnit?: string;
    field?: string;
    notes?: Note[] | null;
    procedureCode?: string;
  };
}

export interface DeleteEligibilityBenefitEditRequestBody {
  networkType: NetworkTypeEnum;
  networkName: string;
  key: EligibilityBenefitEditKeys;
  value: string;
}

export interface DeleteAllCoverageEligibilityBenefitEditRequestBody {
  networkType: NetworkTypeEnum;
  networkName: string;
  procedureCode: string;
}

export type AppointmentEligibilityDataMismatch = {
  patient: {
    fullName: MismatchedField<string>;
    fullAddress: MismatchedField<string>;
    dob: MismatchedField<string>;
  };
  subscriber: {
    fullName: MismatchedField<string>;
    fullAddress: MismatchedField<string>;
    dob: MismatchedField<string>;
  };
  groupNumber: MismatchedField<string>;
};

export type MismatchedField<T> = {
  appointmentValue: T | null;
  eligibilityValue: T | null;
  isMismatched: boolean;
};
